import { isValidElement, type MouseEvent, type ReactNode } from 'react';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import { BadgeAlert, CheckCircle2, Info, LoaderCircle, TriangleAlert, X } from 'lucide-react';
import { toast, type Action } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from 'src/components/ui/avatar';
import { cn } from 'src/lib/utils';

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface ToastAvatar {
  src?: string;
  alt?: string;
  fallback: string;
}

interface ToastWithAvatarProps {
  toastId: string | number;
  title: ReactNode;
  description?: ReactNode;
  variant: ToastVariant;
  avatar: ToastAvatar;
  action?: Action | ReactNode;
  cancel?: Action | ReactNode;
  dismissible?: boolean;
}

const variantIcons = {
  default: BadgeAlert,
  success: CheckCircle2,
  error: BadgeAlert,
  warning: TriangleAlert,
  info: Info,
  loading: LoaderCircle,
} satisfies Record<ToastVariant, typeof CheckCircle2>;

function isToastAction(action: Action | ReactNode): action is Action {
  return Boolean(
    action &&
    typeof action === 'object' &&
    !isValidElement(action) &&
    'label' in action &&
    'onClick' in action
  );
}

function getVariantPalette(
  variant: ToastVariant,
  mode: 'light' | 'dark',
  palette: Theme['palette']
) {
  if (variant === 'success') {
    return palette.success;
  }

  if (variant === 'error') {
    return palette.error;
  }

  if (variant === 'warning') {
    return palette.warning;
  }

  if (variant === 'info') {
    return palette.info;
  }

  if (variant === 'loading') {
    return {
      main: palette.secondary.main,
      dark: palette.secondary.dark,
      light: palette.secondary.light,
      contrastText: palette.secondary.contrastText,
    };
  }

  return {
    main: palette.primary.main,
    dark: palette.primary.dark,
    light: mode === 'dark' ? palette.primary.light : palette.primary.dark,
    contrastText: palette.primary.contrastText,
  };
}

function renderAction(
  action: Action | ReactNode | undefined,
  tone: ReturnType<typeof getVariantPalette>,
  toastId: string | number,
  variant: 'action' | 'cancel'
) {
  if (!action) {
    return null;
  }

  if (isValidElement(action)) {
    return action;
  }

  if (!isToastAction(action)) {
    return null;
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    action.onClick(event);

    if (!event.defaultPrevented) {
      toast.dismiss(toastId);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
        variant === 'action'
          ? 'text-white hover:brightness-105'
          : 'border border-solid hover:bg-black/5 dark:hover:bg-white/5'
      )}
      style={
        variant === 'action'
          ? {
              backgroundColor: tone.main,
            }
          : {
              borderColor: alpha(tone.main, 0.24),
              color: tone.dark,
            }
      }
    >
      {action.label}
    </button>
  );
}

export function ToastWithAvatar({
  toastId,
  title,
  description,
  variant,
  avatar,
  action,
  cancel,
  dismissible = true,
}: ToastWithAvatarProps) {
  const theme = useTheme();
  const tone = getVariantPalette(variant, theme.palette.mode, theme.palette);
  const StatusIcon = variantIcons[variant];

  return (
    <div
      className="relative flex w-[min(24rem,calc(100vw-2rem))] items-start gap-3 rounded-2xl border px-4 py-3 shadow-lg backdrop-blur-sm"
      style={{
        backgroundColor:
          theme.palette.mode === 'dark'
            ? alpha(tone.main, 0.14)
            : alpha(tone.main, variant === 'default' ? 0.08 : 0.12),
        borderColor: alpha(tone.main, 0.24),
        color: theme.palette.text.primary,
      }}
    >
      <div className="relative shrink-0">
        <Avatar
          className="h-10 w-10 border shadow-sm"
          style={{
            borderColor: alpha(tone.main, 0.24),
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {avatar.src ? <AvatarImage src={avatar.src} alt={avatar.alt ?? avatar.fallback} /> : null}
          <AvatarFallback
            className="text-xs font-semibold uppercase"
            style={{
              backgroundColor: alpha(tone.main, 0.12),
              color: tone.dark,
            }}
          >
            {avatar.fallback}
          </AvatarFallback>
        </Avatar>
        <span
          className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2"
          style={{
            backgroundColor: tone.main,
            borderColor: theme.palette.background.paper,
            color: tone.contrastText,
          }}
        >
          <StatusIcon className={cn('h-3 w-3', variant === 'loading' && 'animate-spin')} />
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="pr-8 text-sm font-semibold">{title}</div>
        {description ? (
          <div className="mt-1 text-sm text-muted-foreground">{description}</div>
        ) : null}
        {action || cancel ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {renderAction(cancel, tone, toastId, 'cancel')}
            {renderAction(action, tone, toastId, 'action')}
          </div>
        ) : null}
      </div>

      {dismissible ? (
        <button
          type="button"
          onClick={() => toast.dismiss(toastId)}
          className="absolute right-3 top-3 rounded-full p-1 transition-colors hover:bg-black/5 dark:hover:bg-white/5"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}
